/**
 * Постраничный список блока «Голоса клиента» (#2314): по `pageSize` строк,
 * «Предыдущий / Далее» и «11–20 из 30».
 *
 * - Новые строки (другой период, проект, поток) — список с начала: с
 *   первой страницы или со страницы строки, открытой по адресу.
 * - Те же строки — страница зрителя; её меняет только строка, которую
 *   открыли или сдвинули, — не та, что осталась открытой, когда другую закрыли.
 * - Перелистывание возвращает начало списка в поле зрения: раскрытая карточка
 *   на прежней странице могла быть выше экрана.
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useShownKeys, type VoiceTopListProps } from './voice-top-list';

/** A row to reveal and the page it is on, as `page:key`. */
function placedRows<T>(
  rows: readonly T[],
  pageSize: number,
  rowKey: (row: T) => string,
  keys: readonly (string | null | undefined)[]
): { page: number; mark: string }[] {
  return keys.flatMap((key) => {
    const index = key == null ? -1 : rows.findIndex((row) => rowKey(row) === key);
    if (index < 0) return [];
    const page = Math.floor(index / pageSize);
    return [{ page, mark: `${page}:${key}` }];
  });
}

export function VoicePagedList<T>({
  rows,
  pageSize,
  label,
  rowKey,
  revealKeys = [],
  renderRow,
  onShownKeys,
  list = 'div',
  gap = 'space-y-2',
}: VoiceTopListProps<T> & { pageSize: number }) {
  const { t } = useTranslation(['dashboard', 'common']);
  const top = useRef<HTMLDivElement>(null);
  const pages = Math.max(1, Math.ceil(rows.length / pageSize));
  const rowsMark = rows.map(rowKey).join('\n');
  const placed = placedRows(rows, pageSize, rowKey, revealKeys);
  const marks = placed.map((row) => row.mark);
  const [page, setPage] = useState(placed[0]?.page ?? 0);
  const [seen, setSeen] = useState({ rowsMark, marks });
  let current = page;
  if (rowsMark !== seen.rowsMark || marks.join('\n') !== seen.marks.join('\n')) {
    const fresh = placed.find((row) => !seen.marks.includes(row.mark));
    current = rowsMark !== seen.rowsMark ? (placed[0]?.page ?? 0) : (fresh?.page ?? page);
    setSeen({ rowsMark, marks });
    if (current !== page) setPage(current);
  }
  const from = current * pageSize;
  const visible = rows.slice(from, from + pageSize);
  useShownKeys(visible, rowKey, onShownKeys);
  const List = list;

  const turn = (next: number) => {
    if (next < 0 || next >= pages) return;
    setPage(next);
    const box = top.current;
    if (box && box.getBoundingClientRect().top < 0)
      box.scrollIntoView?.({ block: 'start' });
  };
  // `aria-disabled`, not `disabled`: the button the viewer pressed to reach
  // the last page keeps the keyboard focus.
  const edge = 'aria-disabled:cursor-not-allowed aria-disabled:opacity-50';

  return (
    <div ref={top} className="scroll-mt-32">
      <List className={gap}>{visible.map(renderRow)}</List>
      {pages > 1 && (
        <nav aria-label={label} className="mt-2 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            className={`gap-1 ${edge}`}
            aria-disabled={current === 0}
            onClick={() => turn(current - 1)}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            {t('common:common.previous')}
          </Button>
          <span className="text-xs text-fg-muted">
            {t('voicePage.layers.pageRange', {
              from: from + 1,
              to: from + visible.length,
              total: rows.length,
            })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className={`gap-1 ${edge}`}
            aria-disabled={current >= pages - 1}
            onClick={() => turn(current + 1)}
          >
            {t('common:common.next')}
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
        </nav>
      )}
    </div>
  );
}
